import React, { useState } from 'react';
import { Checkbox, Modal } from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faEnvelopeOpenText, faLock, faPhoneAlt, faShieldAlt, faUnlink } from '@fortawesome/free-solid-svg-icons';

import { BlueButton } from '@share/components';
import { RootState, disableCruiseSecurityReminder, shouldDisplayCruiseSecurityReminder } from '@share/utils';

import './style.scss';

interface ITip {
  key: string;
  icon: IconDefinition;
}

const TIPS: ITip[] = [
  { key: 'verify', icon: faEnvelopeOpenText },
  { key: 'links', icon: faUnlink },
  { key: 'protect', icon: faLock },
  { key: 'reach', icon: faPhoneAlt },
];

export function CruiseSecurityModal(): React.ReactElement {
  const account = useSelector((state: RootState) => state.loginStore.account);

  const [visible, setVisible] = useState<boolean>(() => shouldDisplayCruiseSecurityReminder());
  const [dontShowAgain, setDontShowAgain] = useState<boolean>(false);

  const fontFamily = account?.fontFamily;
  const secondaryFontFamily = account?.secondaryFontFamily;

  const handleClose = () => {
    if (dontShowAgain) {
      disableCruiseSecurityReminder();
    }
    setVisible(false);
  };

  const handleDontShowAgainChange = (e: CheckboxChangeEvent) => setDontShowAgain(e.target.checked);

  return (
    <Modal
      className={`cruise-security-modal ${fontFamily ? `${fontFamily}-font` : ''} ${secondaryFontFamily ? `secondary-${secondaryFontFamily}-font` : ''}`}
      wrapClassName="cruise-security-modal__wrapper"
      open={visible}
      footer={null}
      centered
      width={560}
      onCancel={handleClose}
    >
      <div className="cruise-security-modal__header">
        <div className="cruise-security-modal__badge" aria-hidden="true">
          <FontAwesomeIcon icon={faShieldAlt} />
        </div>
        <div>
          <p className="cruise-security-modal__eyebrow">
            <FormattedMessage id="cruise.security.reminder.eyebrow" />
          </p>
          <h4 className="cruise-security-modal__title secondary-font">
            <FormattedMessage id="cruise.security.reminder.title" />
          </h4>
        </div>
      </div>

      <div className="cruise-security-modal__body">
        <p>
          <FormattedMessage id="cruise.security.reminder.intro" />
        </p>

        <ul className="cruise-security-modal__tips">
          {TIPS.map(({ key, icon }) => (
            <li key={key} className="cruise-security-modal__tip">
              <FontAwesomeIcon icon={icon} className="cruise-security-modal__tip-icon" aria-hidden="true" />
              <span>
                <strong className="cruise-security-modal__tip-title">
                  <FormattedMessage id={`cruise.security.reminder.tip.${key}.title`} />
                </strong>{' '}
                <FormattedMessage id={`cruise.security.reminder.tip.${key}.text`} />
              </span>
            </li>
          ))}
        </ul>

        <p>
          <FormattedMessage id="cruise.security.reminder.closing" />
        </p>
        <p className="cruise-security-modal__signature">
          <FormattedMessage id="cruise.security.reminder.signature" />
        </p>
      </div>

      <div className="cruise-security-modal__footer">
        <Checkbox checked={dontShowAgain} onChange={handleDontShowAgainChange}>
          <FormattedMessage id="cruise.security.reminder.dont.show.again" />
        </Checkbox>
        <BlueButton onClick={handleClose}>
          <FormattedMessage id="cruise.security.reminder.got.it" />
        </BlueButton>
      </div>
    </Modal>
  );
}
